import {
  Country,
  CountryCreditRiskLifecycle,
  CountryKycCheck,
  CountryPartyType
} from "../../../api/Api";
import { HubSyncValues } from "./utils";
import { Progress } from "../departmentProgress/utils";

/**
 * Applying an accepted sync to a country. The counterparty and broker version is applyPatch.ts; this
 * is separate rather than a third branch of it because almost nothing carries over.
 *
 * Where the four syncable values live on a country:
 *
 *   kycCheck        complianceLifecycle.kycChecks[party].status
 *   creditCheck     creditRiskLifecycle.creditCheckTso    | creditCheckExchange
 *   creditAmount    creditRiskLifecycle.creditAmountTso   | creditAmountExchange  (free text, not a number)
 *   creditCurrency  creditRiskLifecycle.creditCurrencyTso | creditCurrencyExchange
 *
 * So a country differs from the other two books in two ways at once: the KYC status is one of a
 * collection keyed by party rather than a single field, and there are two credit lines rather than
 * one. None of the ownership rules in applyPatch.ts survive that, and pretending otherwise would give
 * the file a set of branches that agree on nothing.
 */

/** Accepted values per party, for one country. Absent party means nothing accepted for it. */
export type CountryPartyPatches = Partial<
  Record<CountryPartyType, Partial<HubSyncValues>>
>;

/** The two parties a country can hold a credit line towards. The rest have no credit fields. */
const CREDIT_FIELDS: Partial<
  Record<
    CountryPartyType,
    {
      check: keyof CountryCreditRiskLifecycle;
      amount: keyof CountryCreditRiskLifecycle;
      currency: keyof CountryCreditRiskLifecycle;
    }
  >
> = {
  tso: {
    check: "creditCheckTso",
    amount: "creditAmountTso",
    currency: "creditCurrencyTso"
  },
  exchange: {
    check: "creditCheckExchange",
    amount: "creditAmountExchange",
    currency: "creditCurrencyExchange"
  }
};

/**
 * Whether a party has a credit line in the Hub at all. The bulk page is told this by the backend,
 * which reads the same rule off the country model; the Compliance dialog has to ask here.
 */
export const partySupportsCredit = (partyType?: CountryPartyType) =>
  !!partyType && !!CREDIT_FIELDS[partyType];

/**
 * The credit line one party holds on the credit risk lifecycle, all three fields empty when it has
 * none. The inverse of countryCreditRiskChangesFor, and the reason both live here: the TSO-or-exchange
 * mapping is read in one place and written in another, and they have to agree.
 */
export const countryCreditFor = (
  creditRisk: CountryCreditRiskLifecycle | undefined,
  partyType: CountryPartyType
): { check?: string; amount?: string; currency?: string } => {
  const fields = CREDIT_FIELDS[partyType];

  if (!fields || !creditRisk) return {};

  return {
    check: creditRisk[fields.check] as string | undefined,
    amount: creditRisk[fields.amount] as string | undefined,
    currency: creditRisk[fields.currency] as string | undefined
  };
};

/**
 * One party's accepted credit values as a credit-risk-lifecycle patch, empty when the party has no
 * credit line or nothing credit-shaped was accepted.
 *
 * Shared by the bulk apply below and by the Compliance dialog, which stages the same change into
 * local state instead of writing it: both have to put a TSO's limit in the TSO fields, and a second
 * copy of that mapping is a second thing to get wrong.
 */
export const countryCreditRiskChangesFor = (
  partyType: CountryPartyType,
  patch: Partial<HubSyncValues>
): Partial<CountryCreditRiskLifecycle> => {
  const fields = CREDIT_FIELDS[partyType];

  if (!fields) return {};

  return {
    ...(patch.creditCheck !== undefined
      ? { [fields.check]: patch.creditCheck }
      : {}),
    // The Hub stores country credit amounts as free text, unlike the counterparty amount which is a
    // number. The warehouse value is a number, so it is stringified on the way in rather than
    // formatted: a thousands separator here would come straight back as unparseable next time.
    ...(patch.creditAmount !== undefined
      ? { [fields.amount]: String(patch.creditAmount) }
      : {}),
    ...(patch.creditCurrency !== undefined
      ? { [fields.currency]: patch.creditCurrency }
      : {})
  };
};

/**
 * Reads a stored country credit amount back as a number, for comparing against the warehouse column.
 * Mirrors CountryWarehouseSync.ParseAmount on the backend, which does this for the bulk page; the
 * Compliance dialog reads the stored country itself and so needs it here.
 *
 * Anything that will not parse reads as no amount, which surfaces as a difference and offers the
 * warehouse number in its place. An unparseable amount is not a value the Hub can be said to hold.
 */
export const parseCountryCreditAmount = (value?: string | null) => {
  const trimmed = value?.trim();

  if (!trimmed) return undefined;

  const parsed = Number(trimmed.replace(/,/g, ""));

  return Number.isFinite(parsed) ? parsed : undefined;
};

const CREDIT_RISK_OWNED_FIELDS: (keyof HubSyncValues)[] = [
  "creditCheck",
  "creditAmount",
  "creditCurrency"
];

const touchesCreditRisk = (patch: Partial<HubSyncValues>) =>
  CREDIT_RISK_OWNED_FIELDS.some((field) => patch[field] !== undefined);

const touchesCompliance = (patch: Partial<HubSyncValues>) =>
  patch.kycCheck !== undefined;

/**
 * Where the Compliance lifecycle's progress lands once a sync has filled in its data: completed when
 * nothing Compliance owes is outstanding, which for a country means every row in both KYC blocks has
 * an answer and REMIT reporting has been decided. That is the same condition the Compliance dialog
 * gates its Complete button on, read off the rows the sync is about to write.
 *
 * Same restraint as the counterparty version: this moves progress.compliance only and never
 * complianceLifecycle.complete, because the backend worker emails a lifecycle's assignees when that
 * flag flips false to true, and a whole-book sweep would become one email per country.
 */
export const syncedCountryComplianceProgress = (
  country: Country,
  patchedKycChecks: CountryKycCheck[]
) => {
  const onMft = country.complianceLifecycle?.kycChecksOnMft ?? [];
  const answered = [...patchedKycChecks, ...onMft].every((check) =>
    Boolean(check.status)
  );

  return answered && country.complianceLifecycle?.reporting
    ? Progress.Completed
    : Progress.InProgress;
};

/**
 * Where the Credit Risk lifecycle's progress lands, evaluated against the credit risk slice the sync
 * is about to write. Completed when every credit check the country actually needs is answered, which
 * is the country Credit Risk dialog's own Complete rule.
 *
 * Before the Risk / Credit Risk split this also required the mandate to be answered, because the
 * mandate shared the lifecycle. It now lives on Risk alone, so it no longer holds this one back.
 */
export const syncedCountryCreditRiskProgress = (
  country: Country,
  patchedCreditRisk: CountryCreditRiskLifecycle
) => {
  const marketAccess = country.marketAccessLifecycle;
  const tsoSettled =
    marketAccess?.tso !== "needed" || Boolean(patchedCreditRisk.creditCheckTso);
  const exchangeSettled =
    marketAccess?.exchange !== "needed" ||
    Boolean(patchedCreditRisk.creditCheckExchange);

  return tsoSettled && exchangeSettled
    ? Progress.Completed
    : Progress.InProgress;
};

/**
 * Applies the accepted differences for one country, however many of its parties they cover, and
 * returns a new country. The input is not mutated.
 *
 * Taking every party at once is deliberate. The bulk page shows a country as one row per party, but
 * they are one document: applying them one at a time would be four read-modify-writes, four queue
 * messages and four summary upserts for one country, and each write would have to be sequenced
 * against the last to avoid reverting it.
 *
 * `catalystSyncedAt` is stamped on every party the patch covers, including one where only credit was
 * accepted: the stamp belongs to the id, and the id is on the KYC row.
 */
export const applyCountrySyncPatch = (
  country: Country,
  partyPatches: CountryPartyPatches,
  syncedAt: string
): Country => {
  const patchedParties = Object.keys(partyPatches) as CountryPartyType[];

  // Rows are matched on party type, never on position: the Compliance dialog rebuilds this
  // collection from the Market Access selection every time it opens.
  const kycChecks = (country.complianceLifecycle?.kycChecks ?? []).map(
    (check) => {
      const patch = check.partyType ? partyPatches[check.partyType] : undefined;

      if (!patch) return check;

      return {
        ...check,
        ...(patch.kycCheck !== undefined ? { status: patch.kycCheck } : {}),
        catalystSyncedAt: syncedAt
      };
    }
  );

  const creditRiskChanges = patchedParties.reduce<
    Partial<CountryCreditRiskLifecycle>
  >((changes, partyType) => {
    const patch = partyPatches[partyType];

    // A party with no credit line cannot have had a credit field accepted: buildSyncRows marks
    // those rows unsyncable, and countryCreditRiskChangesFor returns nothing for it either way.
    return patch
      ? { ...changes, ...countryCreditRiskChangesFor(partyType, patch) }
      : changes;
  }, {});

  const patchedCreditRisk: CountryCreditRiskLifecycle = {
    ...country.creditRiskLifecycle,
    ...creditRiskChanges
  };

  const patches = patchedParties
    .map((partyType) => partyPatches[partyType])
    .filter((patch): patch is Partial<HubSyncValues> => !!patch);

  // Each lifecycle's progress moves only when the patch actually reached data that lifecycle owns,
  // so a credit-only sync never restates Compliance's progress and a KYC-only sync never restates
  // Credit Risk's.
  const progressChanges = {
    ...(patches.some(touchesCreditRisk)
      ? {
          creditRisk: syncedCountryCreditRiskProgress(
            country,
            patchedCreditRisk
          )
        }
      : {}),
    ...(patches.some(touchesCompliance)
      ? { compliance: syncedCountryComplianceProgress(country, kycChecks) }
      : {})
  };

  return {
    ...country,
    complianceLifecycle: {
      ...country.complianceLifecycle,
      kycChecks
    },
    // Only touch the credit risk slice when the patch reached it, so an untouched lifecycle is not
    // rewritten with an identical copy of itself on every apply.
    ...(Object.keys(creditRiskChanges).length
      ? { creditRiskLifecycle: patchedCreditRisk }
      : {}),
    ...(Object.keys(progressChanges).length
      ? { progress: { ...country.progress, ...progressChanges } }
      : {})
  };
};
